import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchBasicPost, fetchBasicSocialPost, fetchPost as fetchPostAPI, fetchSocialPost, fetchSocialPosts } from "services/api/influencer";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { BasicSocialPost, InfluencerInsights, influencerSlice, MediaProductType, Post, Posts, SocialPost } from "types/influencer";
import { getErrorMessage, isFresh } from "utils/helpers";

// export const fetchInfluencerInsights = createAsyncThunk<{influencerId: string, insightsData: InfluencerInsights}, {force?: boolean, influencerId: string}, {rejectValue: string}>(
//     'influencer/insights',
//     async ({force, influencerId}, {getState, rejectWithValue}) => {
//         try {
//             const STALE_TIME = apiConfig.FETCH_INFLUENCER_INSIGHTS.staleTime;
//             const state = getState() as {auth: AuthState, influencers: influencerSlice};
//             const agencyId = state.auth.selectedAgencyId ?? '';
//             if(!force && isFresh(state.influencers.insights?.[influencerId]?.last_fetched_at ?? null, STALE_TIME)){
//                 return rejectWithValue(ERROR.CACHED);
//             }

//             const insightsData = await fetchInfluencerInsightsAPI(agencyId, influencerId);

//             return {insightsData, influencerId}

//         } catch (error) {
//             return rejectWithValue(getErrorMessage(error, "Failed to fetch influencer insights"));
//         }
//     }
// ) 


export const fetchPost = createAsyncThunk<{influencerId: string, post: Post, postId: string}, {force?: boolean, influencerId: string, postId: string}, {rejectValue: string}>(
    'influencer/post',
    async ({force, influencerId, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_INFLUENCER_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            const hubId = state.auth.selectedHubId ?? '';
            if(!force && isFresh(state.influencers.posts?.[influencerId]?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchPostAPI(hubId, influencerId, postId);

            return {post, postId, influencerId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 


export const fetchBasicPostThunk = createAsyncThunk<{post: Post, postId: string}, {force?: boolean, postId: string}, {rejectValue: string}>(
    'influencer/basicPost',
    async ({force, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_BASIC_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            if(!force && isFresh(state.influencers.basic_posts?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const post = await fetchBasicPost(postId);

            return {post, postId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 

// export const fetchMedia = createAsyncThunk<{influencerId: string, posts: Posts, mediaProductType: 'reels' | 'posts', page: number}, {influencerId: string, mediaProductType : "reels" | "posts", page: number, force?: boolean }, {rejectValue: string}>(
//     'influencer/media',
//     async ({force, influencerId, mediaProductType, page}, {getState, rejectWithValue}) => {
//         try {
//             const state = getState() as {auth: AuthState, influencers: influencerSlice};
//             const STALE_TIME = apiConfig.FETCH_INFLUENCER_MEDIA.staleTime;

//             if(page === 1 && !force && isFresh(state.influencers.media?.[influencerId]?.[mediaProductType]?.last_fetched_at ?? null, STALE_TIME)){
//                 return rejectWithValue(ERROR.CACHED);
//             }

//             const agencyId = state.auth.selectedAgencyId ?? '';
//             const posts = await fetchMediaAPI(agencyId, influencerId, {page, sort: 'timestamp', media_product_type: mediaProductType});

//             return {posts, influencerId, mediaProductType, page}

//         } catch (error) {
//             return rejectWithValue(getErrorMessage(error, "Failed to fetch media!"))
//         }
//     }
// )


export const fetchBasicSocialPostThunk = createAsyncThunk<{post: BasicSocialPost, postId: string}, {force?: boolean, postId: string}, {rejectValue: string}>(
    'influencer/basic-social-post',
    async ({force, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_BASIC_SOCIAL_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            if(!force && isFresh(state.influencers.basic_social_posts?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const hubId = state.auth.selectedHubId ?? '';
            if(!hubId) {
                return rejectWithValue("Hub not selected");
            }

            const post = await fetchBasicSocialPost(hubId, postId);

            return {post, postId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 


export const fetchSocialPostThunk = createAsyncThunk<{socialAccountId: string, post: SocialPost, postId: string}, {force?: boolean, socialAccountId: string, postId: string}, {rejectValue: string}>(
    'social-account/social-post',
    async ({force, socialAccountId, postId}, {getState, rejectWithValue}) => {
        try {
            const STALE_TIME = apiConfig.FETCH_SOCIAL_POST.staleTime;
            const state = getState() as {auth: AuthState, influencers: influencerSlice};
            if(!force && isFresh(state.influencers.social_posts?.[socialAccountId]?.[postId]?.last_fetched_at ?? null, STALE_TIME)){
                return rejectWithValue(ERROR.CACHED);
            }

            const hubId = state.auth.selectedHubId ?? '';
            if(!hubId) {
                return rejectWithValue("Hub not selected");
            }

            const post = await fetchSocialPost(hubId, socialAccountId, postId);

            return {post, postId, socialAccountId}

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Failed to fetch post"));
        }
    }
) 


export const fetchSocialPostsThunk = createAsyncThunk(
    'influencer/social-posts',
    async (data: { socialAccountId: string, mediaProductType: MediaProductType, page: number, force?: boolean }, { getState, rejectWithValue }) => {
        const state = getState() as { influencers: influencerSlice };
        const mediaType = data.mediaProductType;
        const socialAccountId = data.socialAccountId;
        const staleTime = apiConfig.FETCH_SOCIAL_POSTS.staleTime;
        if (data.page === 1 && !data.force && isFresh(state.influencers.social_posts?.[socialAccountId]?.[mediaType]?.last_fetched_at, staleTime )) {
            return rejectWithValue(ERROR.CACHED);
        }

        const hubId = (getState() as {auth: AuthState}).auth.selectedHubId ?? '';
        if(!hubId) {
            return rejectWithValue("Hub not selected");
        }

        try {
            const media = await fetchSocialPosts(hubId, socialAccountId, mediaType, data.page);
            return { media, ...data };
        } catch (error) {
            console.log(error)
            return rejectWithValue(ERROR.NOT_FOUND);
        }
    }
)
